import React from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import { IImage } from 'tbk-components/src/components/Image';
import AdvancedMedia from '../AdvancedMedia';
import AnimatedArcs from '../AnimatedArcs';

interface IListLink {
	/**
	 * The link title will override the label
	 */
	label?: string;
	link?: ILink;
}

interface IInfoBlock {
	heading?: string;
	/**
	 * @control richtext
	 */
	excerpt?: string;
	listItems?: IListLink[];
	cta?: ILink;
}

export interface IInfoListProps extends IBaseElementProps {
	title?: string;
	/**
	 * @control richtext
	 */
	excerpt?: string;
	infoBlocks?: IInfoBlock[];
	backgroundImage?: IImage;
}

/**
 * Calian Info List
 * @icon editor-insertmore
 * @block
 */
const InfoList: React.FC<IInfoListProps> = ({
	id,
	className,
	classNames = [],
	title,
	excerpt,
	infoBlocks = [],
	backgroundImage,
}) => {
	return (
		<div
			id={id}
			className={cn(
				className || [
					'calian-info-list',
					'bg-light position-relative z-0',
					...classNames,
				],
			)}
		>
			<div className="calian-info-list__container z-1 px-3 px-lg-0 position-relative mw-1240px mx-auto py-6 py-xl-12">
				<div className="calian-info-list__content gap-7 gap-md-7 gap-xl-5">
					<div className="calian-info-list__head d-flex flex-column gap-3 px-lg-5">
						{title && <h2 className="mb-0">{title}</h2>}
						{excerpt && (
							<p
								className={cn('mb-0', !title && 'text-body-lg')}
								dangerouslySetInnerHTML={{
									__html: excerpt,
								}}
							/>
						)}
					</div>
					{infoBlocks.length > 0 &&
						infoBlocks.map((infoBlock, index) => {
							return (
								<div
									key={index}
									className="calian-info-list-block px-lg-5 d-flex flex-column gap-2"
								>
									{infoBlock.heading && (
										<h3 className="mb-2">
											{infoBlock.heading}
										</h3>
									)}
									{infoBlock.excerpt && (
										<p
											className={cn(
												'mb-0',
												!infoBlock.heading &&
													'text-body-lg',
											)}
											dangerouslySetInnerHTML={{
												__html: infoBlock.excerpt,
											}}
										/>
									)}
									{infoBlock.listItems && (
										<ul className="list-unstyled mb-0 d-flex gap-3 flex-column">
											{infoBlock.listItems.map(
												(listItem, index) => {
													return (
														<li
															key={index}
															className="d-flex position-relative align-items-center gap-2"
														>
															{listItem.label &&
																!listItem.link
																	?.label && (
																	<span className="mb-0">
																		{
																			listItem.label
																		}
																	</span>
																)}
															{listItem.link && (
																<Button
																	{...listItem.link}
																	className="mb-0 text-charcoal"
																/>
															)}
														</li>
													);
												},
											)}
										</ul>
									)}
									{infoBlock.cta && (
										<Button
											{...infoBlock.cta}
											label=""
											className="mb-0 h4 text-decoration-none text-primary calian-info-list-block__cta"
											icon="arrow-right"
										/>
									)}
								</div>
							);
						})}
				</div>
			</div>
			{backgroundImage && (
				<div className="calian-info-list__bg position-absolute top-0 start-0 z-0 w-100 h-100">
					<AdvancedMedia
						classNames={[
							'position-absolute top-0 w-50 end-0 z-0 opacity-50 h-100',
						]}
						image={backgroundImage}
						mediaSize="cover"
					/>
				</div>
			)}
			<div className="d-none d-md-block">
				<AnimatedArcs
					arcsStyle={'Component - Two Lines'}
					arcsPosition="Right"
					arcOneColor="var(--bs-primary)"
					arcOneStrokeWidth={3}
					arcTwoColor="var(--bs-white)"
					arcTwoStrokeWidth={3}
					animationDelay={1000}
					animationOffset={150}
				/>
			</div>
		</div>
	);
};

export default InfoList;
